import React, { useState, useEffect, useRef } from 'react';
import { trackEvent, ClarityEvents } from '../utils/clarityTracking';

const CYCLING_WORDS = ['a Religion', 'a Society', 'a Culture', 'a Customer'];

// Timings (ms)
const WORD_ANIM_MS      = 2000; // each cycling word enter→hold→exit
const HEADLINE_SHRINK_MS = 3400; // "You are an iPhone User": enter big → hold → shrink small
// % split of HEADLINE_SHRINK_MS:
//   0–22%  (748ms)  enter
//   22–60% (1292ms) hold big
//   60–82% (748ms)  shrink + rise
//   82–100% (612ms) hold small (tagline visible by now)
const TAGLINE_DELAY_MS  = HEADLINE_SHRINK_MS * 0.60; // starts exactly when shrink begins
const TAGLINE_DUR_MS    = HEADLINE_SHRINK_MS * 0.40 + 1200; // covers rest of headline + extra hold

const PremiumCard: React.FC = () => {
  const cardRef                   = useRef<HTMLDivElement>(null);
  const [started, setStarted]     = useState(false);
  const [wordIndex, setWordIndex] = useState(0);

  const isFinal = wordIndex === CYCLING_WORDS.length;

  /* ── Start when 40% of card enters viewport ── */
  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setStarted(true); observer.disconnect(); } },
      { threshold: 0.4 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  /* ── Advance cycling words ── */
  const handleWordEnd = () => {
    if (wordIndex < CYCLING_WORDS.length - 1) {
      setWordIndex(prev => prev + 1);
    } else {
      setWordIndex(CYCLING_WORDS.length); // enter final phase
    }
  };

  /* ── Tagline animation done → loop back ── */
  const handleTaglineEnd = () => {
    setWordIndex(0);
  };

  return (
    <>
      <style>{`
        @keyframes wordEnterExit {
          0%   { transform: translateY(40px); opacity: 0; filter: blur(8px); }
          18%  { transform: translateY(0);    opacity: 1; filter: blur(0);   }
          72%  { transform: translateY(0);    opacity: 1; filter: blur(0);   }
          90%  { transform: translateY(-40px);opacity: 0; filter: blur(8px); }
          100% { transform: translateY(-40px);opacity: 0; filter: blur(8px); }
        }

        /* Headline: enters big, holds, then shrinks & rises to make room for tagline */
        @keyframes headlineShrink {
          0%   { transform: translateY(44px) scale(0.88); opacity: 0; filter: blur(10px); }
          22%  { transform: translateY(0)    scale(1);    opacity: 1; filter: blur(0);    }
          60%  { transform: translateY(0)    scale(1);    opacity: 1; filter: blur(0);    }
          82%  { transform: translateY(-64px) scale(0.52); opacity: 0.75; filter: blur(0); }
          100% { transform: translateY(-64px) scale(0.52); opacity: 0.75; filter: blur(0); }
        }

        /* Tagline: delayed enter + hold + fade out */
        @keyframes taglineBigReveal {
          0%   { transform: translateY(36px); opacity: 0; filter: blur(6px); }
          20%  { transform: translateY(20px); opacity: 1; filter: blur(0);   }
          75%  { transform: translateY(20px); opacity: 1; filter: blur(0);   }
          100% { transform: translateY(8px);  opacity: 0; filter: blur(4px); }
        }

        @keyframes cardPulse {
          0%, 100% { box-shadow: 0 8px 36px rgba(192,0,122,0.4);  }
          50%       { box-shadow: 0 8px 56px rgba(192,0,122,0.65); }
        }
      `}</style>

      <div className="mb-6">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-1 h-5 bg-minto-500 rounded-full" />
          <h2 className="text-base md:text-lg font-semibold text-gray-800">You are Premium</h2>
        </div>

        <a
          href="https://www.mintorepairs.com/brand/1"
          onClick={() => trackEvent(ClarityEvents.IPHONE_BANNER_CLICK, { source: 'premium_card' })}
          className="block"
        >
          <div
            ref={cardRef}
            className="relative rounded-2xl overflow-hidden cursor-pointer text-center"
            style={{
              height: '260px',
              background: 'linear-gradient(135deg, #d4007a 0%, #7a0050 100%)',
              animation: 'cardPulse 4s ease-in-out infinite',
            }}
          >
            {/* ── Cycling layer ── */}
            <div
              style={{
                position: 'absolute', inset: 0,
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center',
                opacity: isFinal ? 0 : 1,
                pointerEvents: 'none',
              }}
            >
              <p className="text-white/70 text-lg font-light tracking-wide mb-2">
                You are more than
              </p>
              <div className="h-14 flex items-center justify-center overflow-hidden">
                {started && !isFinal && (
                  <span
                    key={wordIndex}
                    className="text-white text-4xl font-extrabold leading-none tracking-tight"
                    style={{
                      animation: `wordEnterExit ${WORD_ANIM_MS}ms cubic-bezier(0.22, 1, 0.36, 1) forwards`,
                      willChange: 'transform, opacity, filter',
                    }}
                    onAnimationEnd={handleWordEnd}
                  >
                    {CYCLING_WORDS[wordIndex]}
                  </span>
                )}
              </div>
            </div>

            {/* ── Final layer: headline shrinks, tagline grows in ── */}
            {isFinal && (
              <div
                style={{
                  position: 'absolute', inset: 0,
                  display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center',
                  pointerEvents: 'none',
                }}
              >
                {/* Headline — enters big, then shrinks upward */}
                <span
                  key="final-headline"
                  className="text-white font-extrabold leading-tight tracking-tight"
                  style={{
                    fontSize: 'clamp(2rem, 7vw, 2.8rem)',
                    animation: `headlineShrink ${HEADLINE_SHRINK_MS}ms cubic-bezier(0.22, 1, 0.36, 1) forwards`,
                    willChange: 'transform, opacity, filter',
                    display: 'block',
                  }}
                >
                  You are an
                  <br />
                  <span style={{ fontSize: 'clamp(2.4rem, 9vw, 3.2rem)' }}>iPhone User</span>
                </span>

                {/* Tagline — delayed, enters big, holds, fades out → triggers loop */}
                <span
                  key="final-tagline"
                  className="text-white font-bold tracking-tight"
                  style={{
                    position: 'absolute',
                    fontSize: 'clamp(1.2rem, 4vw, 1.6rem)',
                    animation: `taglineBigReveal ${TAGLINE_DUR_MS}ms cubic-bezier(0.22, 1, 0.36, 1) ${TAGLINE_DELAY_MS}ms forwards`,
                    opacity: 0,
                    willChange: 'transform, opacity, filter',
                  }}
                  onAnimationEnd={handleTaglineEnd}
                >
                  Redefine your premiumness
                  <br />
                  <span style={{ fontSize: 'clamp(0.85rem, 2.5vw, 1rem)', fontWeight: 400, opacity: 0.85 }}>
                    in minutes — Minto
                  </span>
                </span>
              </div>
            )}
          </div>
        </a>
      </div>
    </>
  );
};

export default PremiumCard;
